// LivestreamConfig.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { FaSearch, FaPlus, FaChevronLeft } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { 
  getLivestreamsPaginados,
  createLivestream, 
  updateLivestream, 
  deleteLivestream,
  toggleLivestreamStatus
} from '../../../components/http/Actions/LivestreamAction';

// Components
import LivestreamTable from './components/LivestreamTable';
import EditLivestreamModal from './components/EditLivestreamModal';
import CreateLivestreamModal from './components/CreateLivestreamModal';
import ToggleLivestreamStatusModal from './components/ToggleLivestreamStatusModal';

import Notification from '../../../components/Notification';
import Pagination from '../../../components/Pagination';
import SearchBar from '../../../components/SearchBar';

const LivestreamConfig = () => {
  // Estado para almacenar la lista de livestreams
  const [livestreams, setLivestreams] = useState([]);
  
  // Estado para búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  // Estado para paginación
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 5,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  });
  
  // Estados para modales
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [livestreamToEdit, setLivestreamToEdit] = useState(null);
  const [livestreamToToggle, setLivestreamToToggle] = useState(null);
  
  // Estado para nuevo livestream
  const [newLivestream, setNewLivestream] = useState({
    titulo: '',
    descripcion: '',
    fecha: '',
    imagen: '',
    precio: 0,
    estado: 'Próximo', // Próximo, EnVivo, Finalizado
    videoURL: '',
    ComedianteIds: [], // Explicitly initialize as an empty array
    TagIds: []         // Explicitly initialize as an empty array
  });
  
  // token
  const token = localStorage.getItem("token");
  // Estado para errores y notificaciones
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [isLoading, setIsLoading] = useState(false);

  // Efecto para debounce en la búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Función para mostrar notificación
  const showNotification = useCallback((message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  }, []);

  // Función para cargar livestreams con paginación
  const fetchLivestreams = useCallback(async (page = 1, pageSize = 5, search = '') => {
    setIsLoading(true);
    try {
      await getLivestreamsPaginados(
        (data) => {
          if (data && data.data) {
            setLivestreams(data.data.data);
            setPagination({
              currentPage: data.data.currentPage,
              pageSize: data.data.pageSize,
              totalCount: data.data.totalCount,
              totalPages: data.data.totalPages,
              hasNext: data.data.hasNext,
              hasPrevious: data.data.hasPrevious
            });
          } else {
            console.error("La respuesta de la API no tiene el formato esperado:", data);
            setLivestreams([]);
          }
        },
        setError,
        page,
        pageSize,
        search,
        true, // includeDisabled para el admin
        token
      );
    } catch (error) {
      console.error("Error fetching livestreams:", error);
      showNotification("Error al cargar los livestreams", "error");
    } finally {
      setIsLoading(false);
    }
  }, [showNotification, token]);

  // Función para cambiar de página
  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
  };
  
  // Función para buscar
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  };

  // Cargar livestreams cuando cambia la página o el término de búsqueda
  useEffect(() => {
    fetchLivestreams(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
  }, [pagination.currentPage, debouncedSearchTerm, fetchLivestreams, pagination.pageSize]);

  // Función para abrir modal de edición
  const openEditModal = (livestream) => {
    setLivestreamToEdit({
      ...livestream,
      fecha: livestream.fecha ? new Date(livestream.fecha).toISOString().split('T')[0] : '',
      ComedianteIds: livestream.comediantes 
        ? livestream.comediantes
            .map(c => c.comedianteId)
            .filter(id => id !== undefined && id !== null) 
        : [],
      TagIds: livestream.tags 
        ? livestream.tags
            .map(t => t.tagId)
            .filter(id => id !== undefined && id !== null)
        : []
    });
    setShowEditModal(true);
  };

  // Función para abrir modal de creación
  const openCreateModal = () => {
    setNewLivestream({
      titulo: '',
      descripcion: '',
      fecha: '',
      imagen: '',
      precio: 0,
      estado: 'Próximo',
      videoURL: '',
      ComedianteIds: [],
      TagIds: []
    });
    setShowCreateModal(true);
  };

  // Función para editar livestream
  const handleEditSubmit = async (e, editedLivestream) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await updateLivestream(editedLivestream.livestreamId, editedLivestream, setError, token);
      fetchLivestreams(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
      showNotification(`Livestream "${editedLivestream.titulo}" actualizado con éxito`, "success");
      setShowEditModal(false);
    } catch (error) {
      console.error("Error updating livestream:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Función para crear livestream
  const handleCreateSubmit = async (e, newLivestreamData) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await createLivestream(newLivestreamData, setError, token);
      fetchLivestreams(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
      showNotification(`Livestream "${newLivestreamData.titulo}" creado con éxito`, "success");
      setShowCreateModal(false);
    } catch (error) {
      console.error("Error creating livestream:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Función para eliminar livestream
  const handleDeleteLivestream = async (livestreamId, titulo) => {
    if (window.confirm(`¿Estás seguro de eliminar el livestream "${titulo}"?`)) {
      setIsLoading(true);
      
      try {
        await deleteLivestream(livestreamId, setError, token);
        fetchLivestreams(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
        showNotification(`Livestream "${titulo}" eliminado con éxito`, "success");
      } catch (error) {
        console.error("Error deleting livestream:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Función para abrir el modal de cambio de estado
  const handleToggleStatusClick = (livestream) => {
    setLivestreamToToggle(livestream);
    setShowToggleModal(true);
  };

  // Función para cambiar estado del livestream (habilitar/deshabilitar)
  const handleToggleStatus = async (livestream) => {
    setIsLoading(true);
    
    try {
      await toggleLivestreamStatus(livestream.livestreamId, setError, token);
      fetchLivestreams(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
      showNotification(`Livestream "${livestream.titulo}" ${!livestream.habilitado ? "habilitado" : "deshabilitado"} con éxito`, "success");
      setShowToggleModal(false);
    } catch (error) {
      console.error(`Error al cambiar estado del livestream:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-900">
      {/* Header */}
      <header className="bg-zinc-800 shadow-lg">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center">
            <Link to="/admin" className="mr-4">
              <FaChevronLeft className="text-white hover:text-red-500 transition-colors" />
            </Link>
            <h1 className="text-3xl font-bold text-white">Gestor de Livestreams</h1>
          </div>
          <button 
            onClick={openCreateModal}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center"
          >
            <FaPlus className="mr-2" />
            Nuevo Livestream
          </button>
        </div>
      </header>

      {/* Notification */}
      {notification.show && (
        <Notification message={notification.message} type={notification.type} />
      )}

      {/* Main content */}
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Search */}
        <SearchBar 
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          placeholder="Buscar por título, comediante o tag..."
        />

        {/* Loading state */}
        {isLoading && (
          <div className="flex justify-center my-12">
            <div className="w-12 h-12 border-4 border-t-4 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="bg-red-500/20 border border-red-500 text-red-100 px-6 py-4 rounded-lg mb-6">
            <p>{error}</p>
          </div>
        )}

        {/* Livestreams table */}
        {!isLoading && !error && (
          <LivestreamTable 
            livestreams={livestreams} 
            onEdit={openEditModal} 
            onDelete={handleDeleteLivestream} 
            onToggleStatus={handleToggleStatusClick}
          />
        )}

        {/* Paginación */}
        {!isLoading && !error && pagination.totalCount > 0 && (
          <Pagination 
            pagination={pagination} 
            handlePageChange={handlePageChange} 
          />
        )}
      </main>

      {/* Modals */}
      {showEditModal && livestreamToEdit && (
        <EditLivestreamModal 
          livestreamToEdit={livestreamToEdit}
          setLivestreamToEdit={setLivestreamToEdit}
          handleEditSubmit={handleEditSubmit}
          isLoading={isLoading}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {showCreateModal && (
        <CreateLivestreamModal 
          newLivestream={newLivestream}
          setNewLivestream={setNewLivestream}
          handleCreateSubmit={handleCreateSubmit}
          isLoading={isLoading}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {showToggleModal && livestreamToToggle && (
        <ToggleLivestreamStatusModal 
          livestream={livestreamToToggle}
          onConfirm={handleToggleStatus}
          onClose={() => setShowToggleModal(false)}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default LivestreamConfig;